import { defineStore } from "pinia";
import { useGeneralStore } from "%/stores/general-store";
import { useStoryStore } from "%/dynamic-component-editor/stores/story-store";
import {
  NAMED_SAVED_POSTS,
  collectionNameProblem,
  savedCollectionsOf,
  savedStatusFromAnswer,
  statusBatches,
} from "%/dynamic-component-editor/helpers/saved-posts.mjs";

const STATUS_BATCH_WINDOW_MS = 50;
const ITEM_CONTEXT_STORY = "story";
const MSG_STATUS_FAILED = "Which posts the reader saved did not load, so their bookmarks show as not saved";
const MSG_SAVE_FAILED = "A post's save did not go through, so its bookmark was put back";
const MSG_COLLECTIONS_FAILED = "The reader's saved collections did not load";
const MSG_FILING_FAILED = "Filing a saved post into a collection did not go through";
const READER_SAVE_FAILED = "That didn't save. Try again.";
const READER_COLLECTIONS_FAILED = "Your collections didn't load. Try again.";

const waitingForStatus = new Set();
let statusTimer = null;

function logFailure(message, context, error) {
  // eslint-disable-next-line no-console
  console.error(message, { ...context, error: error?.response?.data ?? error?.message ?? error });
}

function emptyChooser() {
  return { storyId: 0, collections: {}, loading: false, busyCollectionId: 0, error: "" };
}

export const useSavedPostsStore = defineStore("dynamic-component-editor-saved-posts-store", {
  state: () => ({
    savedByStory: {},
    pendingByStory: {},
    chooser: emptyChooser(),
    readerCollections: {},
    readerCollectionsLoaded: false,
    readerCollectionsError: "",
  }),
  getters: {
    isSaved: state => storyId => state.savedByStory[Number(storyId)] === true,
    chooserCollections: state => savedCollectionsOf(state.chooser.collections),
    savedCollections: state => savedCollectionsOf(state.readerCollections),
  },
  actions: {
    watchSavedStatus(storyId) {
      const id = Number(storyId);
      if (!id || id in this.savedByStory || !useStoryStore().isUserLoggedIn) {
        return;
      }
      waitingForStatus.add(id);
      if (statusTimer === null) {
        statusTimer = setTimeout(() => this.loadWaitingStatus(), STATUS_BATCH_WINDOW_MS);
      }
    },
    loadWaitingStatus() {
      statusTimer = null;
      const asked = [...waitingForStatus];
      waitingForStatus.clear();
      statusBatches(asked).forEach(batch => {
        this.apiGet(
          "/story/saved_among",
          { story_ids: JSON.stringify(batch) },
          answer => Object.assign(this.savedByStory, savedStatusFromAnswer(batch, answer.savedStoryIds)),
          error => logFailure(MSG_STATUS_FAILED, { storyIds: batch }, error),
        );
      });
    },
    tapSave(storyId) {
      const id = Number(storyId);
      const storyStore = useStoryStore();
      if (!storyStore.isUserLoggedIn) {
        storyStore.redirectToLoginToEngage();
        return;
      }
      if (!id || this.pendingByStory[id]) {
        return;
      }
      if (this.isSaved(id)) {
        this.sendSave(id, false);
        return;
      }
      this.sendSave(id, true);
      this.openChooser(id);
    },
    sendSave(storyId, save) {
      const before = this.savedByStory[storyId] === true;
      this.savedByStory[storyId] = save;
      this.pendingByStory[storyId] = true;
      this.apiPost(
        `/story/${storyId}/${save ? "save" : "unsave"}`,
        {},
        () => {
          this.pendingByStory[storyId] = false;
          if (save && this.chooser.storyId === storyId) {
            this.loadChooserCollections();
          }
        },
        error => {
          this.pendingByStory[storyId] = false;
          this.savedByStory[storyId] = before;
          if (this.chooser.storyId === storyId) {
            this.closeChooser();
          }
          logFailure(MSG_SAVE_FAILED, { storyId, save }, error);
        },
      );
    },
    openChooser(storyId) {
      this.chooser = { ...emptyChooser(), storyId: Number(storyId), loading: true };
    },
    closeChooser() {
      this.chooser = emptyChooser();
    },
    loadChooserCollections() {
      const storyId = this.chooser.storyId;
      const orgId = useGeneralStore().orgId;
      this.chooser.loading = true;
      this.chooser.error = "";
      this.apiGet(
        `/collections/${orgId}`,
        { last_id: 0, item_id: storyId, item_context: ITEM_CONTEXT_STORY },
        feed => {
          if (this.chooser.storyId !== storyId) {
            return;
          }
          this.chooser.collections = feed.entities?.collection ?? {};
          this.chooser.loading = false;
        },
        error => {
          this.chooser.loading = false;
          this.chooser.error = READER_COLLECTIONS_FAILED;
          logFailure(MSG_COLLECTIONS_FAILED, { storyId, orgId }, error);
        },
      );
    },
    loadReaderCollections() {
      const orgId = useGeneralStore().orgId;
      this.readerCollectionsError = "";
      this.apiGet(
        `/collections/${orgId}`,
        { last_id: 0, item_id: 0, item_context: ITEM_CONTEXT_STORY },
        feed => {
          this.readerCollections = feed.entities?.collection ?? {};
          this.readerCollectionsLoaded = true;
        },
        error => {
          this.readerCollectionsLoaded = true;
          this.readerCollectionsError = READER_COLLECTIONS_FAILED;
          logFailure(MSG_COLLECTIONS_FAILED, { orgId }, error);
        },
      );
    },
    toggleInCollection(collection) {
      const storyId = this.chooser.storyId;
      const collectionId = collection.collectionId;
      const holds = Number(collection.collectionItemId) > 0;
      this.chooser.busyCollectionId = collectionId;
      this.chooser.error = "";
      const done = feed => {
        this.chooser.busyCollectionId = 0;
        Object.assign(this.chooser.collections, feed.entities?.collection ?? {});
      };
      const failed = error => {
        this.chooser.busyCollectionId = 0;
        this.chooser.error = READER_SAVE_FAILED;
        logFailure(MSG_FILING_FAILED, { storyId, collectionId, holds }, error);
      };
      if (holds) {
        this.apiPost(`/collection/item/${collection.collectionItemId}/remove`, {
          collection_id: collectionId, item_id: storyId, item_context: ITEM_CONTEXT_STORY,
        }, done, failed);
        return;
      }
      this.apiPost(`/collection/${collectionId}/item/add`, { item_id: storyId, item_context: ITEM_CONTEXT_STORY }, done, failed);
    },
    createCollection(name) {
      const problem = collectionNameProblem(name);
      if (problem) {
        this.chooser.error = problem;
        return Promise.resolve(false);
      }
      const storyId = this.chooser.storyId;
      this.chooser.busyCollectionId = -1;
      this.chooser.error = "";
      return new Promise(resolve => {
        this.apiPost("/collection/create", {
          org_id: useGeneralStore().orgId,
          name: name.trim(),
          description: "",
          cover_image_url: "",
          visibility: "private",
          context: NAMED_SAVED_POSTS,
          item_id: storyId,
          item_context: ITEM_CONTEXT_STORY,
        }, () => {
          this.chooser.busyCollectionId = 0;
          this.loadChooserCollections();
          resolve(true);
        }, error => {
          this.chooser.busyCollectionId = 0;
          this.chooser.error = READER_SAVE_FAILED;
          logFailure(MSG_FILING_FAILED, { storyId, name }, error);
          resolve(false);
        });
      });
    },
  },
});
